import 'whatwg-fetch'
import * as Sentry from '@sentry/gatsby'
import { builder } from '@builder.io/react'
import { registerBuilderComponents } from './src/utils/Builder/componentRegistry'

import { IS_FOREIGN_COUNTRY } from './src/utils/constants'

// GATSBY_TODO — Remove when gatsby is retired.
// Env var gate: if missing or not 'true', all navigations stay on Gatsby. Safe default (kill switch).
const HYDROGEN_PROXY_ENABLED = process.env.GATSBY_ENABLE_HYDROGEN_PROXY === 'true'

export const onClientEntry = () => {
  builder.init(process.env.GATSBY_BUILDER_PUB_API_KEY)
  registerBuilderComponents()

  // GATSBY_TODO — Remove when gatsby is retired.
  // Nav catch-all (ECO-345): on the US build, force a full document load on any Gatsby SPA navigation to a
  // DIFFERENT path. The request then hits the primary site, where Hydrogen serves its own routes and the CF
  // 404-proxy serves Gatsby's — no allowlist to keep in sync. Same-path (hash/query-only) navs stay SPA.
  // No state bridge: the current Gatsby page is already served THROUGH Hydrogen on myskylight.com, so the
  // root loader has set the first-party cart/promo cookies — they travel on the hard nav.
  // Foreign builds (intl subdomains) are untouched — they never route through Hydrogen. Behind the
  // GATSBY_ENABLE_HYDROGEN_PROXY kill switch so it's a no-op until the CF cutover.
  // ___navigate is upstream of both <Link> clicks and programmatic navigate() calls. Patching here prevents
  // the SPA transition entirely — no render cycle, no content flash. Safe because Gatsby v3 is frozen.
  // Production build assigns ___navigate BEFORE onClientEntry (unlike dev source suggests).
  // Capture the existing value, wrap it, then use defineProperty to guard against re-assignment.
  if (!IS_FOREIGN_COUNTRY && HYDROGEN_PROXY_ENABLED) {
    let originalNavigate = window.___navigate
    const patchedNavigate = (to, options) => {
      try {
        const href = typeof to === `string` ? to : `${to?.pathname || ``}${to?.search || ``}${to?.hash || ``}`
        // Resolve against the current URL so a relative ?query / #hash target keeps the current pathname
        // (a same-path nav) instead of collapsing to `/` and triggering a spurious full reload.
        const target = new URL(href, window.location.href)
        const targetPath = target.pathname.replace(/\/+$/, ``) || `/`
        const currentPath = window.location.pathname.replace(/\/+$/, ``) || `/`
        if (targetPath !== currentPath) {
          window.location.assign(target.href)
          return
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { system: 'cross-site-proxy', component: 'gatsby-navigate-patch' },
        })
        // Fall through to originalNavigate — user gets Gatsby's SPA nav instead of a dead click
      }
      return originalNavigate(to, options) // same-path (hash/query-only) → let Gatsby handle
    }

    window.___navigate = patchedNavigate
    Object.defineProperty(window, `___navigate`, {
      configurable: true,
      enumerable: true,
      set(fn) {
        originalNavigate = fn
      },
      get() {
        return patchedNavigate
      },
    })
  }
}

export const onRouteUpdate = ({ location }) => {
  if (IS_FOREIGN_COUNTRY) {
    const scriptId = 'trustpilot-widget-script'
    const targetStrings = ['the-skylight-frame', 'the-skylight-calendar-15', 'skylight-plus']
    // Function to load the script and initialize the widget
    const loadScript = () => {
      // Remove existing script if it exists
      const existingScript = document.getElementById(scriptId)
      if (existingScript) {
        existingScript.remove()
      }
      // Create a new script element
      const script = document.createElement('script')
      script.id = scriptId
      script.src = '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js'
      script.async = true
      script.onload = () => {
        // Re-initialize the Trustpilot widget after the script loads
        if (window.Trustpilot) {
          window.Trustpilot.loadFromElement(document.querySelector('.trustpilot-widget'), true)
        }
      }
      document.body.appendChild(script)
    }
    if (targetStrings.some((str) => location.pathname.includes(str))) {
      loadScript()
    }
  }
}
